import { useCloseMultipleFlags } from '@actions';
import FinishFlagIcon from '@assets/icons/Icon-awesome-flag-checkered.svg';
import ExternalLinkIcon from '@assets/icons/external-link.svg';
import Modal from '@components/V4/Modal/Modal';
import Table from '@components/V4/Table/Table';
import { Flag, FlagTypes, ModelFlagTypes } from '@shared/common';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { ChangeEvent, useState } from 'react';

const FLAGS_VIEW_MORE_BUTTON_TEXT = {
  [ModelFlagTypes.ENROLLMENT]: 'View Enrollment',
  [ModelFlagTypes.MEETING]: 'View Session',
  [ModelFlagTypes.STUDENT]: 'View Student',
  [ModelFlagTypes.MENTOR]: 'View Mentor',
  [ModelFlagTypes.PARENT]: 'View Parent',
  [ModelFlagTypes.SESSION]: 'View Session',
};

type Props = {
  flagData?: any;
  refetchSearchFlags: () => void;
  currentUserId: string;
  addFlagHandler: (flag: string) => void;
  removeFlagHandler: (flag: string) => void;
  showMoreHandler: (modalType: ModelFlagTypes, flagType: FlagTypes, ref: Flag['ref']) => void;
  openStudentProfileHandler: (studentId: string) => void;
  openMentorProfileHandler: (mentorId: string) => void;
};

const FlagsTableRow = (props: Props) => {
  const {
    flagData,
    refetchSearchFlags,
    currentUserId,
    addFlagHandler,
    removeFlagHandler,
    showMoreHandler,
    openStudentProfileHandler,
    openMentorProfileHandler,
  } = props;

  const { mutate: markAsClosed } = useCloseMultipleFlags({
    onSuccess: () => {
      refetchSearchFlags();
    },
  });

  const checkboxChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const flag = event.target.value;
    if (event.target.checked) {
      addFlagHandler(flag);
    } else {
      removeFlagHandler(flag);
    }
  };

  const viewMoreHandler = () => {
    showMoreHandler(flagData?.modelType, flagData?.flagType, flagData?.ref);
  };

  const markAsClosedHandler = () => {
    markAsClosed({
      flagIds: [flagData?._id],
      closedBy: currentUserId,
    });
  };

  const getStudentName = () => {
    if (
      flagData?.flagType === FlagTypes.LOW_NPS ||
      flagData?.flagType === FlagTypes.DISENGAGED_USER
    ) {
      if ([ModelFlagTypes.STUDENT, ModelFlagTypes.PARENT].includes(flagData?.modelType)) {
        return `${flagData?.ref?.firstName ?? '-'} ${flagData?.ref?.lastName ?? '-'}`;
      } else {
        return '-';
      }
    } else {
      return (
        flagData?.ref?.students?.[0]?.fullName ??
        flagData?.ref?.user?.fullName ??
        `${flagData?.ref?.firstName} ${flagData?.ref?.lastName}`
      );
    }
  };

  const getMentorName = () => {
    if (
      flagData?.flagType === FlagTypes.LOW_NPS ||
      flagData?.flagType === FlagTypes.DISENGAGED_USER
    ) {
      if (flagData?.modelType === ModelFlagTypes.MENTOR) {
        return `${flagData?.ref?.firstName ?? '-'} ${flagData?.ref?.lastName ?? '-'}`;
      } else {
        return '-';
      }
    } else {
      return flagData?.ref?.mentor?.fullName ?? 'TBD';
    }
  };

  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);

  const handleCommentClick = () => {
    if (flagData?.mentorCommentAt && flagData?.mentorComment) {
      setIsCommentModalOpen(true);
    }
  };

  const CommentModal = () => {
    const description =
      flagData?.description && flagData.description.trim().length > 0 ? flagData.description : '-';

    const mentorName = getMentorName();

    const comment =
      flagData?.mentorComment && flagData.mentorComment.trim().length > 0
        ? flagData.mentorComment
        : 'No comments available.';

    const commentedAt = flagData?.mentorCommentAt
      ? moment(flagData.mentorCommentAt).format('DD.MM.YYYY')
      : null;

    return (
      <Modal isOpen={isCommentModalOpen} onClose={() => setIsCommentModalOpen(false)}>
        <Modal.Header>
          <div className="flex flex-col gap-1">
            <span className="font-semibold">Mentor Comment</span>
            <span className="text-sm text-gray-300">
              {mentorName !== '-' ? `From ${mentorName}` : 'From mentor'}
              {commentedAt ? ` • ${commentedAt}` : ''}
            </span>
            <span className="text-sm text-gray-400">
              Flag: <span className="italic">{description}</span>
            </span>
          </div>
        </Modal.Header>

        <Modal.Body>
          <p className="whitespace-pre-wrap">{comment}</p>
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <>
      {isCommentModalOpen && <CommentModal />}
      <Table.Row>
        <Table.Cell className="flex items-center gap-2">
          {!flagData?.closedAt && (
            <input
              onChange={checkboxChangeHandler}
              value={flagData?._id}
              id="flags"
              aria-describedby="comments-description"
              name="flags"
              type="checkbox"
              className={mergeClassNames(
                'h-4 w-4 rounded border-customGrey text-customPrimary focus:ring-customPrimary bg-transparent cursor-pointer'
              )}
            />
          )}
          <span>{flagData?.description ?? '-'}</span>
        </Table.Cell>
        <Table.Cell
          className="cursor-pointer underline"
          onClick={() =>
            openStudentProfileHandler(flagData?.ref?.students?.[0]?._id ?? flagData?.ref?.user?._id)
          }
        >
          {getStudentName()}
        </Table.Cell>
        <Table.Cell
          className="cursor-pointer underline"
          onClick={() => openMentorProfileHandler(flagData?.ref?.mentor?._id)}
        >
          {getMentorName()}
        </Table.Cell>
        <Table.Cell>{flagData?.ref?.course?.title ?? '-'}</Table.Cell>
        <Table.Cell>
          {flagData?.createdAt ? moment(flagData?.createdAt).format('DD.MM.YYYY') : '-'}
        </Table.Cell>
        <Table.Cell
          className={mergeClassNames(flagData?.mentorCommentAt ? 'underline cursor-pointer' : '')}
          onClick={handleCommentClick}
        >
          {flagData?.mentorCommentAt ? moment(flagData?.mentorCommentAt).format('DD.MM.YYYY') : ''}
        </Table.Cell>
        <Table.Cell className="flex gap-4">
          <div onClick={viewMoreHandler} className="flex gap-1 cursor-pointer">
            <img className="w-3.5" src={ExternalLinkIcon} alt="flags" title="View more Flags" />
            <span className="min-w-max text-white">
              {FLAGS_VIEW_MORE_BUTTON_TEXT[flagData.modelType as ModelFlagTypes]}
            </span>
          </div>
          {!flagData?.closedAt && (
            <div onClick={markAsClosedHandler} className="flex cursor-pointer gap-1">
              <img className="files-icon" src={FinishFlagIcon} alt="flag" />
              <span className="min-w-max text-customRedAccent">Mark as closed</span>
            </div>
          )}
        </Table.Cell>
      </Table.Row>
    </>
  );
};

export default FlagsTableRow;
